import { z } from "zod"
import { SalesAddressModelSchema, SalesInvoiceAddressModelSchema } from "./SalesAddressModel"
import { SalesCartProductModelSchema } from "./SalesCartProductModel"
import {
	SalesAgentSchema,
	SalesDateTimeSchema,
	SalesFulfilmentMode,
	SalesOrderStatus,
	SalesSerialNumberMapListSchema,
} from "./SalesCommons"
import { SalesContactModelSchema } from "./SalesContactModel"
import { SalesOrderFulfilmentModelSchema } from "./SalesOrderFulfilmentModel"
import { SalesPaymentModelSchema } from "./SalesPaymentModel"

/**
 * S&O sales Order entity schema
 */
export const SalesOrderModelSchema = z.object({
	id: z.string().min(1),

	payment_reference: z.string().min(1),

	items: z.array(SalesCartProductModelSchema).min(1),

	contact: SalesContactModelSchema.nullable(),

	shipping_address: SalesAddressModelSchema,

	invoice_address: SalesInvoiceAddressModelSchema,

	sales_channel: z.string(),

	payments: z.array(SalesPaymentModelSchema),

	discount_codes: z.array(z.string()),

	sub_total: z.number(),

	vat: z.number(),

	/**
	 * Total items discount
	 */
	items_discount: z.number().min(0),

	delivery: z.number(),

	/**
	 * Total discount amount.
	 * Includes shipping, item and order discounts.
	 *
	 * Excludes the discount offered by bundles, but includes
	 * any discounts applied to bundles from coupons.
	 */
	discount_total: z.number().min(0),

	/**
	 * Total discounts a bundle offers (excludes any coupon discounts)
	 *
	 * A bundle offers a total discounted amount.
	 * This discount field indicates the total discount offered by all the bundles
	 * combined.
	 */
	bundle_savings_total: z.number().min(0),

	/**
	 * Total delivery discount
	 */
	delivery_discount: z.number().min(0),

	total: z.number(),

	status: z.enum([SalesOrderStatus.PaymentPending, SalesOrderStatus.Paid, SalesOrderStatus.Shipped, SalesOrderStatus.Delivered]),

	fulfilment_mode: z.enum([SalesFulfilmentMode.SelfPickup, SalesFulfilmentMode.Delivery]),

	fulfilment: SalesOrderFulfilmentModelSchema,

	sales_agent: SalesAgentSchema.nullable(),

	product_serial_numbers: SalesSerialNumberMapListSchema,

	created_at: SalesDateTimeSchema,

	updated_at: SalesDateTimeSchema,
})

/**
 * Currently, we only support fully paid orders. This means,
 * and order can only exist if all payments have been made.
 *
 * It's also expected that process takes care of populating the correct delivery and shipping details.
 */
export type SalesOrderModel = z.infer<typeof SalesOrderModelSchema>

/**
 * The minimum fields required, along with their validation,
 * that supports recording a payment. We want to create as little dependencies on receiving
 * payments as possible.
 *
 * Any downstream processes that require certain fields not listed here
 * should have its own remedial process.
 */
export const SalesOrderPaymentReadySchema = z.object({
	id: SalesOrderModelSchema.shape.id,
	payment_reference: z.string().min(1),

	/**
	 * We don't want to block payments from being recorded due to invalid
	 * contact details. As long as a contact with an id is linked, we're good.
	 */
	contact: SalesContactModelSchema.pick({ id: true }),

	fulfilment_mode: z.enum([SalesFulfilmentMode.SelfPickup, SalesFulfilmentMode.Delivery]),

	total: z.number().min(0),
})
export type SalesOrderPaymentReady = z.infer<typeof SalesOrderPaymentReadySchema>
